/**
 * AntePost page.
 *
 * @module app/pages/AntePostPage
 */
import _ from 'lodash';
import $ from 'jquery';
import BasePage from './BasePage';
import BodyStateAdapter from '../components/BodyStateAdapter';
import DomPushAdapter from '../components/push/domUpdates/DomPushAdapter';
import LazyLoadingImage from '../components/LazyLoadingImage';
import LiveScore from '../components/LiveScore';
import MessageBusTopics from '../../common/MessageBusTopics';
import ModelComposer from '../components/ModelComposer';
import OffCanvas from '../components/OffCanvas';
import PushAdapter from '../components/push/PushAdapter';
import SelectionViewAdapter from '../views/SelectionViewAdapter';
import URLProvider from '../components/URLProvider';

/**
 * OffCanvas Wrapper jquery element object
 *
 * @type {Object}
 *
 * @private
 */
let $offCanvas = null;

/**
 * Internal state flag used to
 * determine whether page has been
 * rendered or not
 *
 * @type {Boolean}
 *
 * @private
 */
let rendered = false;

/**
 * Page content in the form of a jQuery element
 *
 * @type {Object}
 *
 * @private
 */
let content = null;

/**
 * Page dependencies
 *
 * @type {Object}
 *
 * @private
 */
let deps = null;

/**
 * Page arguments
 *
 * @type {Object}
 *
 * @private
 */
let args = null;

/**
 * Pagination
 *
 * @type {Object}
 */
const pagination = {

    /**
     * Base link
     *
     * @type {String}
     */
    baseLink: null,

    /**
     * XMLHttpRequest
     *
     * @type {XMLHttpRequest}
     */
    request: null,

    /**
     * Row IDs
     *
     * @type {Array}
     */
    rowIds: [],

    /**
     * Lazy Loading initialize
     *
     * @returns {void}
     *
     * @private
     */
    init: () => {

        if (window.WH.sportsbook.totalPages > 1) {

            BasePage.LazyLoadingViewAdapter.init({
                totalPages: window.WH.sportsbook.totalPages,
                container: '.race-event-wrapper',
                group: '.racecards_group_wrapper',
                subGroup: '.racecards_subgroup_wrapper',
                row: '.race-event',
                selection: '.oddsbutton',
                selectionOdds: '.racecard-button__price',
                totalItemsPerPage: window.WH.sportsbook.eventsPerPage,
                pagination: pagination,
                highlightedSelections: SelectionViewAdapter.highlightedSelections,
                timeSelector: '.localisable',
                totalRowsCount: window.WH.sportsbook.totalRowsCount,
            });
        }
    },

    /**
     * Abort Ajax request if needed
     *
     * @returns {void}
     *
     * @private
     */
    abort: () => {

        if (pagination.request && _.isFunction(pagination.request.abort)) {
            pagination.request.abort();
        }
    },

    /**
     * Gets more data to Lazy Load
     *
     * @param {Number}   rowNumber
     * @param {Number}   direction
     * @param {Function} callback
     *
     * @returns {void}
     *
     * @private
     */
    getMoreData: (rowNumber, direction, callback) => {

        const pageNumber = Math.ceil(rowNumber / parseInt(window.WH.sportsbook.eventsPerPage, 10));

        if (pageNumber > 0 && pageNumber <= parseInt(window.WH.sportsbook.totalPages, 10)) {
            pagination.getPage(rowNumber, direction, callback);
        } else {
            callback({ totalRows: 0, compiled: [] }, direction);
        }
    },

    /**
     * Get data for selected page
     *
     * @param   {Number}   rowNumber
     * @param   {Number}   direction
     * @param   {Function} callback
     *
     * @returns {void}
     *
     * @private
     */
    getPage: (rowNumber, direction, errorCallback) => {

        let successCallback = (direction === -1) ? pagination.savePrevPage : pagination.saveNextPage;
        const pageNumber = Math.ceil(rowNumber / parseInt(window.WH.sportsbook.eventsPerPage, 10));
        const pageUrl = `${pagination.baseLink}${pageNumber}.json`;
        pagination.request = deps.pageCommon.getJSONResource(pageUrl, {
            'afterSuccess': _.partial(successCallback, errorCallback, rowNumber),
            'afterError': _.partial(errorCallback, [], direction),
        });
    },


    /**
     * Function to filter already existing data
     *
     * @param   {Object}   data
     * @param   {Number}   rowNumber
     * @param   {Number}   direction
     *
     * @returns {void}
     *
     * @private
     */
    filterPaginationData: (data, rowNumber, direction) => {

        let eventsToFilter = 0;

        if (direction === -1) {
            eventsToFilter = ((rowNumber - 1) % parseInt(window.WH.sportsbook.eventsPerPage, 10)) + 1;
            eventsToFilter = Math.min(parseInt(window.WH.sportsbook.eventsPerPage, 10),
                Object.keys(data.data.allRacesEvents.allModels).length) - eventsToFilter;
        } else {
            eventsToFilter = ((rowNumber - 1) % parseInt(window.WH.sportsbook.eventsPerPage, 10));
        }

        pagination.removeCompiledRows(
            data,
            eventsToFilter,
            direction
        );
    },

    /**
     * Remove compiled rows that we don't need
     *
     * @param   {Object} data
     * @param   {Array} eventsToFilter
     * @param   {Number} direction
     *
     * @returns {void}
     *
     * @private
     */
    removeCompiledRows: (data, eventsToFilter, direction) => {

        if (eventsToFilter > 0) {
            pagination.rowIds = [];
            const rowsToRemove = pagination.rowsToRemove(data, eventsToFilter, direction);

            data.data.compiled[0].rows[0].rows = _.filter(
                data.data.compiled[0].rows[0].rows,
                (row) => !_.includes(rowsToRemove, row.key)
            );
        }
    },

    /**
     * Loop over subgroups
     *
     * @param   {Object} group
     *
     * @returns {void}
     *
     * @private
     */
    groupRows: (group) => _.each(group.rows, pagination.subgroupRowIds),

    /**
     * Loop over rows
     *
     * @param   {Object} subgroup
     *
     * @returns {void}
     *
     * @private
     */
    subgroupRowIds: (subgroup) => {

        pagination.rowIds = pagination.rowIds.concat(_.map(subgroup.rows, 'key'));
    },

    /**
     * Obtain row IDs given the compiled Array
     *
     * @param   {Array} compiled
     *
     * @returns {void}
     *
     * @private
     */
    obtainRowIds: (compiled) => _.each(compiled, pagination.groupRows),

    /**
     * Select rows to be removed
     *
     * @param   {Object} data
     * @param   {Array} eventsToFilter
     * @param   {Number} direction
     *
     * @returns {Array}
     *
     * @private
     */
    rowsToRemove: (data, eventsToFilter, direction) => {

        pagination.obtainRowIds(data.data.compiled);

        if (direction === -1) {
            pagination.rowIds = pagination.rowIds.splice(-1 * eventsToFilter);
        } else {
            pagination.rowIds = pagination.rowIds.splice(0, eventsToFilter);
        }

        _.each(pagination.rowIds, function (eventId) {

            delete data.data.model.events[eventId];
        });

        return pagination.rowIds;
    },

    /**
     * Do model updates
     *
     * @param   {Object} allModels
     *
     * @returns {void}
     *
     * @private
     */
    doModelUpdates: (allModels) => {

        _.each(allModels, (model) => {

            ModelComposer.extendModel(model);
            pagination.updateSubscription(model);
        });
    },

    /**
     * Function called after obtaining next page data to parse it and save it
     *
     * @param   {Function} callback
     * @param   {Number}   rowNumber
     * @param   {Object}   data
     *
     * @returns {void}
     *
     * @private
     */
    saveNextPage: (callback, rowNumber, data) => {

        pagination.filterPaginationData(data, rowNumber, 1);

        pagination.doModelUpdates(data.data.allRacesEvents.allModels);

        if (_.isFunction(callback)) {
            callback({
                totalRows: _.size(data.data.allRacesEvents.allModels),
                compiled: data.data.compiled,
            }, 1);
        }
    },

    /**
     * Function called after obtaining previous page data to parse it and save it
     *
     * @param   {Function} callback
     * @param   {Number}   rowNumber
     * @param   {Object}   data
     *
     * @returns {void}
     *
     * @private
     */
    savePrevPage: (callback, rowNumber, data) => {

        pagination.filterPaginationData(data, rowNumber, -1);

        pagination.doModelUpdates(data.data.allRacesEvents.allModels);

        if (_.isFunction(callback)) {
            callback({
                totalRows: _.size(data.data.allRacesEvents.allModels),
                compiled: data.data.compiled,
            }, -1);
        }
    },
    /**
     * Initializes pagination
     *
     * @returns {void}
     *
     * @private
     */
    initializePagination: () => {

        pagination.baseLink = window.WH.sportsbook.baseLink;
        pagination.init();
    },

    /**
     * This functions handles removing events from model
     *
     * @param   {Array} eventsToRemove
     *
     * @returns {void}
     *
     * @private
     */
    updateModel: (eventsToRemove) => {

        ModelComposer.removeEventsFromModel(eventsToRemove);
    },

    /**
     * Subscribed to given events and their descendands
     *
     * @param   {Object} model
     *
     * @returns {void}
     *
     * @private
     */
    updateSubscription: (model) => {

        _.each(model, push.subscribeSetToPush);
        _.each(model.events, (event) => LiveScore.subscribe(event, content));
    },
};

/**
 * Function used to bind event handles to
 * various buttons/links on the page
 *
 * @param   {Object} content jQuery object containing
 *                           the page content
 * @param   {Object} deps    Page dependencies
 * @param   {Object} args    Page arguments
 *
 * @returns {void}
 *
 * @private
 */
const bindNavigation = (content, deps, args) => {

    initInternals(content, deps, args);
    bindCustomNavigation(content);
};

/**
 * Initialize interface
 *
 * @returns {void}
 *
 * @private
 */
const initInterface = () => {

    const model = deps.sportsDataRepository.getModel();

    SelectionViewAdapter.init(model.selections, content);

    _.each(model.events, (event) => LiveScore.subscribe(event, content));
    pagination.initializePagination();
};

/**
 * Init model for later usage
 *
 * @params {Boolean} regenerateModel
 *
 * @returns {void}
 *
 * @private
 */
const initModel = (regenerateModel) => {

    BasePage.setOddsFormat();

    if (regenerateModel === true) {
        ModelComposer.regenerateMergedModel();
    }

    PushAdapter.unsubscribeAll();
    _.each(deps.sportsDataRepository.getModel(), push.subscribeSetToPush);
};

/**
 * Perform initalization of model and interface
 *
 * @params {Boolean} regenerateModel
 *
 * @returns {void}
 *
 * @private
 */
const init = (regenerateModel) => {

    initModel(regenerateModel);
    initInterface();
    LazyLoadingImage.init();
};

/**
 * Init internal data for later usage
 *
 * @param   {Object} content jQuery container element
 * @param   {Object} deps    Page dependencies
 * @param   {Object} args    Page arguments
 *
 * @returns {void}
 *
 * @private
 */
const initInternals = (pageContent, pageDeps, pageArgs) => {

    content = pageContent;
    deps = pageDeps;
    args = pageArgs;
};

/**
 * Function used to load the data for the allRaces button
 * We bind this function to the allRaces function
 * This will get the partial from the server and render in the race card
 *
 * @returns {void}
 *
 * @private
 */
const allRacesClickAction = function () {

    const allRacesLinkElement = $(this);

    args.competitionId = allRacesLinkElement.attr('data-competitionId');

    /**
     * What to do after a successful reload of snippet.
     *
     * @returns {void}
     *
     * @private
     */
    const afterSuccess = () => {

        init(true);
        OffCanvas.hideLeftPanel($offCanvas);

        // Update the filter's status
        content.find('.race-group-content__item-link--selected')
            .removeClass('race-group-content__item-link--selected');
        allRacesLinkElement.addClass('race-group-content__item-link--selected');
        window.WH.messageBus.publish(MessageBusTopics.NAVIGATION_PAGE_CHANGED);

        BasePage.scrollTop();
    };

    const competitionName = $(`.race-subnav__group#${args.competitionId} .race-subnav__group-header-name`).text();
    const redirectArgs = _.extend({}, args, { competitionName });

    // reload the snippet and set the url and history
    deps.pageCommon.reloadSnippet({
        'requestURL': URLProvider.antePost.allRaces(args),
        'redirectURL': URLProvider.antePost.allRaces(redirectArgs, true),
    }, '.race-event-wrapper', { afterSuccess });
};

/**
 * Function used to show/hide the runner overview details that was selected
 *
 * @param   {Object} evt action event
 *
 * @returns {Boolean}
 *
 * @private
 */
const showRunnerOverviewDetails = (evt) => {

    const selection = $(evt.target).parents('article').first();
    const selectionOverviewDetails = selection.find('.racecard-runner__extra-details');

    selectionOverviewDetails.toggleClass('-expanded');

    return false;
};

/**
 * Function used to load the data for a racecard
 * based on the clicked meetingNav element
 *
 * @returns {void}
 *
 * @private
 */
const racecardClickAction = function() {

    const racecardLinkElement = $(this);
    args.raceId = racecardLinkElement.data('entityid');

    /**
     * What to do after a successful reload of snippet.
     *
     * @returns {void}
     *
     * @private
     */
    const afterSuccess = () => {

        init(true);
        OffCanvas.hideLeftPanel($offCanvas);

        // Update the filter's status
        content.find('.race-group-content__item-link--selected')
            .removeClass('race-group-content__item-link--selected');
        racecardLinkElement.addClass('race-group-content__item-link--selected');
        window.WH.messageBus.publish(MessageBusTopics.NAVIGATION_PAGE_CHANGED);

        BasePage.scrollTop();
    };

    /**
     * Pre-render action
     *
     * @param {Object} data
     *
     * @returns {void}
     *
     * @private
     */
    const preRender = (data) => {

        data = $(data);

        if (data.find('.race-event__name h1').text().trim() === '') {
            data.find('.race-event__name h1').text(racecardLinkElement.data('racename'));
        }

        return data.closest('.race-event-wrapper').html();
    };

    const raceName = racecardLinkElement.data('racename');
    const redirectArgs = _.extend({}, args, { raceName });

    // reload the snippet and set the url and history
    deps.pageCommon.reloadSnippet({
        'requestURL': URLProvider.antePost.racecard(args),
        'redirectURL': URLProvider.antePost.racecard(redirectArgs, true),
    }, '.race-event-wrapper', { afterSuccess, preRender });
};

/**
 * Function used for binding custom behavior
 * to buttons or links on the page
 *
 * @param   {Object} content jQuery element
 *
 * @returns {void}
 *
 * @private
 */
const bindCustomNavigation = (content) => {

    const bindings = [{
        selector: '.race-group-content__item-link[data-entityid]',
        clickGroup: 'racecardClickAction',
        bindFunctions: [ racecardClickAction ],
    }, {
        selector: '.race-group-content__item-allRaces',
        clickGroup: 'allRacesClickAction',
        bindFunctions: [ allRacesClickAction ],
    }, {
        selector: '.racecard-runner__name',
        clickGroup: 'showRunnerOverviewDetails',
        bindFunctions: [ showRunnerOverviewDetails ],
    }];
    _.each(bindings, _.partial(applyBindings, content));
};

/**
 * Binds functions to the given binding Object
 *
 * @param {Object} content jQuery element
 * @param {Object} binding Object with required attributes:
 *                              {String} selector jQuery selector
 *                              {String} clickGroup Event Namespace
 *                              {Array} bindFunctions An Array of functions to bind to a click event
 *
 * @returns {void}
 *
 * @private
 */
const applyBindings = (content, binding) => {

    const selector = binding.selector;
    const clickGroup = binding.clickGroup;

    content.find(selector).off('click.' + clickGroup);

    _.each(binding.bindFunctions, (bindFunction) => {
        content.find(selector).on('click.' + clickGroup, bindFunction);
    });
};

/**
 * Push related functionality
 *
 * @type {Object}
 */
const push = {
    /**
     * Subscribe a set of entities (events, markets, etc)
     * to PUSH updates
     *
     * @param   {Object} set An object containing entities
     *                       such as markets, selections, events
     *
     * @returns {void}
     *
     * @private
     */
    subscribeSetToPush: (set) =>_.each(set, push.subscribeEntityToPush),
    /**
     * Subscribe a single entity to PUSH updates
     *
     * @param   {Object} entity Market/event/selection model
     *
     * @returns {void}
     *
     * @private
     */
    subscribeEntityToPush: (entity) => {

        if (entity && entity.id && !entity.id.match(/OB_TY/g) && !entity.settled &&
            DomPushAdapter.subscribe(entity, push.callback)) {
            PushAdapter.subscribe(entity.pdsTopic);
        }
    },

    /**
     * DOM PUSH update callback function
     * This function is called every time a push
     * message comes in
     *
     * @param {Object} message DOM PUSH message
     *
     * @private
     */
    callback: (message) => {

        if (push.handlers.hasOwnProperty(message.attribute)) {
            push[push.handlers[message.attribute]](message);
        }
    },

    /**
     * PUSH handlers available per attribute type
     *
     * @type {Object}
     */
    handlers: {},
};

/**
 * Contains functions exposed by this component
 * as an API
 *
 * @type {Object}
 */
const api = {

    bindNavigation: bindNavigation,

    /**
     * Return the URL associated with this page
     * based on the args
     *
     * @param   {Object} args Page arguments
     *
     * @returns {String}      Compiled URL based on args
     *
     * @public
     */
    getContentURL: (args) => {

        let base = `/betting/${args.locale}/${args.sport}/future-races`;

        if (args.raceId && !args.competitionId) {
            base = `/betting/${args.locale}/${args.sport}/OB_EV${args.raceId}`;
        }

        return base;
    },

    /**
     * Function called after the DOM ready event
     * is fired
     *
     * @param   {Object} deps Object that contains page
     *                        dependencies
     *
     * @returns {void}
     *
     * @public
     */
    postReady: (deps, content, args) => {

        BodyStateAdapter.init($, 'antePost', args.sport);

        if (deps.ready) {
            deps.ready('antePost');
        }
    },

    /**
     * Function called after rendering the page
     *
     * @returns {void}
     *
     * @public
     */
    postRender: () => {

        BasePage.switchTab('AntePost');
        init();
    },

    /**
     * Changes rendered state flag to true
     *
     * @public
     */
    setRendered: () => {

        rendered = true;
        $offCanvas = $('#antePost_canvas');
    },

    /**
     * Changes rendered state flag to false
     *
     * @public
     */
    setUnrendered: () => {

        content = deps = args = $offCanvas = null;
        rendered = false;
    },

    /**
     * Returns the value of the internal
     * rendered state flag
     *
     * @returns {Boolean} Returns true or false
     *
     * @public
     */
    isRendered: () => rendered,

    /**
     * Slug to identify the current page
     */
    id: 'antepost',
};

export default api;
